import { ActionType, Selectors } from 'Reducers'
import {
  IUscRegisterAndRedeemAction,
  IUscSignInAndRedeemAction,
  IUscSocialSignInAndRedeemAction,
  IUscSubmitCodeAction
} from 'Reducers/UscRedux'
import { ApiService, PromoCodeService } from 'Services'
import { all, call, put, select } from 'typed-redux-saga'
import { takeLatestSafe } from 'Sagas/SagaUtils'
import { waitForOwnAuthOutcome } from 'Sagas/HelperSagas'
import { push } from 'connected-react-router'
import { RouteNames } from 'RouteNames'
// Straight from the module, not the `Lib` barrel: that one pulls in the service
// layer, which sagas must not drag into their dependency graph.
import { promoCodeErrorKey } from 'Lib/UscUtils'
import { PromoCodeValidationError } from 'types/uscTypes'

/**
 * Step 1 — pre-check the voucher code before we ask the member for an account.
 * Read-only: nothing is redeemed until they are signed in.
 */
function * handleUscValidateCodeSaga({ code, resolve, reject }: IUscSubmitCodeAction) {
  try {
    yield call(logEvent, 'web_usc_validateCode')

    const result: Awaited<ReturnType<typeof PromoCodeService.validateCode>> = yield call(PromoCodeService.validateCode, code)

    if (!result.valid) {
      yield call(logEvent, 'web_usc_validateCodeRejected', { error: result.error })
      // Drop any earlier accepted code, or the member could reach /usc/account and
      // redeem the previous one after this rejection.
      yield put({ type: ActionType.USC_SET_VALIDATED_CODE, code: null, benefit: null })
      yield call(reject, (result.error ?? 'invalid_code') as PromoCodeValidationError)
      return
    }

    yield call(logEvent, 'web_usc_validateCodeSuccess')
    yield put({ type: ActionType.USC_SET_VALIDATED_CODE, code, benefit: result.benefit })
    yield call(resolve)
    yield put(push(RouteNames.USC_ACCOUNT))
  } catch (error) {
    yield call(logError, 'web_usc_validateCodeError', error as Error)
    yield put({ type: ActionType.USC_SET_VALIDATED_CODE, code: null, benefit: null })
    yield call(reject, error as Error)
  }
}

/** Returns to step 1 with the reason in hand. */
function * sendBackToCodeEntrySaga(errorKey: string) {
  // Order matters: dropping the code first re-renders the account screen into its
  // `<Redirect>` guard, which unmounts it before it can report anything. The code
  // entry screen picks the reason up from the store instead, so the member is never
  // bounced back a step without being told why.
  yield put({ type: ActionType.USC_SET_REDEEM_ERROR, errorKey })
  yield put(push(RouteNames.USC_REDEEM))
  yield put({ type: ActionType.USC_SET_VALIDATED_CODE, code: null, benefit: null })
}

/**
 * Step 3 — redeem for the now-signed-in member and land on the download screen.
 *
 * The pre-check in step 1 cannot see per-user state, so the code can still be
 * refused here (`already_redeemed`, `already_has_active_voucher`, or the last
 * redemption taken meanwhile). In
 * that case we drop the code and send them back to entry rather than showing a
 * success screen for a grant they did not get.
 */
function * redeemAndFinishSaga(reject: (error: Error) => void) {
  const code: ReturnType<typeof Selectors.getUscValidatedCode> = yield select(Selectors.getUscValidatedCode)

  if (!code) {
    yield call(logError, 'web_usc_redeemMissingCode', 'No validated code in state')
    yield * sendBackToCodeEntrySaga('usc.errors.network')
    return
  }

  try {
    const result: Awaited<ReturnType<typeof PromoCodeService.redeemCode>> = yield call(PromoCodeService.redeemCode, code)

    if (!result.valid) {
      yield call(logEvent, 'web_usc_redeemRejected', { error: result.error })
      const reason = (result.error ?? 'invalid_code') as PromoCodeValidationError
      yield call(reject, Error(reason))
      yield * sendBackToCodeEntrySaga(promoCodeErrorKey(reason))
      return
    }

    yield call(logEvent, 'web_usc_redeemSuccess', { duration: result.benefit?.freeCoachDuration })
    yield put(push(RouteNames.USC_DOWNLOAD))
  } catch (error) {
    yield call(logError, 'web_usc_redeemError', error as Error)
    yield call(reject, error as Error)
  }
}

/** Step 2b — existing account: sign in, then redeem. */
function * handleUscSignInAndRedeemSaga({ email, password, reject }: IUscSignInAndRedeemAction) {
  yield put({
    type: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_REQUEST,
    email,
    password,
    onError: () => { },
    onSuccess: () => { }
  })

  const outcome = yield * waitForOwnAuthOutcome({
    successType: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_SUCCESS,
    failedType: ActionType.SIGN_IN_WITH_EMAIL_AND_PASSWORD_FAILED,
    email,
    routePrefix: RouteNames.USC
  })

  if (outcome.status === 'abandoned') {
    yield call(logEvent, 'web_usc_signInAbandoned')
    return
  }

  if (outcome.status === 'failed') {
    const error = outcome.error ?? Error('sign_in_failed')
    yield call(logError, 'web_usc_signInError', error)
    yield call(reject, error)
    return
  }

  yield call(logEvent, 'web_usc_signInSuccess')
  yield * redeemAndFinishSaga(reject)
}

type SocialOutcome =
  | { status: 'success' }
  | { status: 'failed', error?: Error }
  | { status: 'cancelled' }

/**
 * Step 2b — Google or Apple: sign in with the provider, then redeem.
 *
 * The provider sagas answer through the callbacks handed to them, so this hand-off
 * is already scoped to our own request — unlike the email waits it does not have to
 * filter app-wide auth actions. Closing the popup is reported separately, otherwise
 * the button would keep spinning after the member backed out.
 */
function * handleUscSocialSignInAndRedeemSaga({ provider, reject, onCancel }: IUscSocialSignInAndRedeemAction) {
  let settle: (outcome: SocialOutcome) => void = () => { }
  const finished = new Promise<SocialOutcome>(resolve => { settle = resolve })

  yield put({
    type: provider === 'apple' ? ActionType.SIGN_IN_WITH_APPLE_REQUEST : ActionType.SIGN_IN_WITH_GOOGLE_REQUEST,
    onSuccess: () => settle({ status: 'success' }),
    onError: (error: Error) => settle({ status: 'failed', error }),
    onCancel: () => settle({ status: 'cancelled' })
  })

  const outcome = yield * call(async () => await finished)

  if (outcome.status === 'cancelled') {
    yield call(logEvent, 'web_usc_socialSignInCancelled', { provider })
    yield call(onCancel)
    return
  }

  if (outcome.status === 'failed') {
    const error = outcome.error ?? Error('social_sign_in_failed')
    yield call(logError, 'web_usc_socialSignInError', error)
    yield call(reject, error)
    return
  }

  yield call(logEvent, 'web_usc_socialSignInSuccess', { provider })
  yield * redeemAndFinishSaga(reject)
}

/** Step 2b — new account: register, then redeem. */
function * handleUscRegisterAndRedeemSaga({ name, email, password, reject }: IUscRegisterAndRedeemAction) {
  yield put({
    type: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD,
    name,
    email,
    password,
    reject: () => { },
    resolve: () => { }
  })

  const outcome = yield * waitForOwnAuthOutcome({
    successType: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD_SUCCESS,
    failedType: ActionType.REGISTER_WITH_EMAIL_AND_PASSWORD_FAILED,
    email,
    routePrefix: RouteNames.USC
  })

  if (outcome.status === 'abandoned') {
    yield call(logEvent, 'web_usc_registerAbandoned')
    return
  }

  if (outcome.status === 'failed') {
    const error = outcome.error ?? Error('register_failed')
    yield call(logError, 'web_usc_registerError', error)
    yield call(reject, error)
    return
  }

  yield call(logEvent, 'web_usc_registerSuccess')
  yield * redeemAndFinishSaga(reject)
}

export function * UscSagas() {
  yield all([
    takeLatestSafe(ActionType.USC_SUBMIT_CODE, handleUscValidateCodeSaga),
    takeLatestSafe(ActionType.USC_SIGN_IN_AND_REDEEM, handleUscSignInAndRedeemSaga),
    takeLatestSafe(ActionType.USC_REGISTER_AND_REDEEM, handleUscRegisterAndRedeemSaga),
    takeLatestSafe(ActionType.USC_SOCIAL_SIGN_IN_AND_REDEEM, handleUscSocialSignInAndRedeemSaga)
  ])
}
